import { MODAL_CONTAINER_ID } from '@egw/constants'
import { Transition } from '@headlessui/react'
import { XMarkIcon, ChevronLeftIcon } from '@heroicons/react/24/solid'
import cx from 'classnames'
import { Fragment, type FC } from 'react'
import { twMerge } from 'tailwind-merge'
import { type DefaultProps } from '../common'
import { Portal } from '../portal/portal'

export interface DialogProps extends DefaultProps {
  /**
   * ModalDrawer open state
   */
  isOpen: boolean
  /**
   * Click handler
   */
  onClose?: () => void
  /**
   * Hide top bar, title and close button
   */
  hideTopBar?: boolean
  /**
   * Is dialog stretch to full screen
   */
  isFullScreen?: boolean
}

export const Dialog: FC<DialogProps> = ({
  onClose,
  isOpen,
  className,
  id,
  testId,
  ariaLabel,
  children,
  hideTopBar = false,
  isFullScreen = true,
}) => {
  // Component base class names. Can be overriden by className property.
  const baseClass = cx('relative z-50')

  // Final class names
  const finalClasses = twMerge(baseClass, className)

  return (
    <Portal containerId={MODAL_CONTAINER_ID}>
      <Transition.Root show={isOpen} as={Fragment}>
        <div
          {...(testId ? { 'data-testid': testId } : {})}
          {...(id ? { id } : {})}
          {...(ariaLabel ? { 'aria-label': ariaLabel } : {})}
          className={finalClasses}
        >
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div
              role="button"
              tabIndex={0}
              onClick={onClose}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  onClose && onClose()
                }
              }}
              className="fixed inset-0 bg-zinc-900 bg-opacity-75 transition-opacity"
            />
          </Transition.Child>

          {/* Modal */}
          <div className="fixed inset-0 flex items-center justify-center md:p-8">
            <Transition.Child
              as={Fragment}
              enter="ease-in-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in-out duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div
                className={cx(
                  'pointer-events-auto transform overflow-hidden rounded-lg bg-white shadow-xl transition-all',
                  {
                    'w-full h-full': isFullScreen,
                    'm-md': !isFullScreen,
                  },
                )}
              >
                {!hideTopBar && (
                  <DialogHeader
                    onClose={
                      onClose
                        ? onClose
                        : () => {
                            // emptry function
                          }
                    }
                  />
                )}
                <div className="px-6 py-4">{children}</div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Transition.Root>
    </Portal>
  )
}

const DialogHeader: FC<{ onClose: () => void }> = ({ onClose }) => {
  return (
    <div className="flex w-full justify-between px-2 border-b border-[#eaeaea]">
      <button
        type="button"
        id="backButton"
        data-testid="backButton"
        className="size-10 p-2.5"
        onClick={onClose}
        aria-label="Close modal"
      >
        <ChevronLeftIcon className="text-iconmuted h-5 w-5" />
      </button>
      <button
        type="button"
        id="closeModal"
        data-testid="closeModal"
        className="size-10 p-2.5"
        onClick={onClose}
        aria-label="Close modal"
      >
        <XMarkIcon className="text-iconmuted h-5 w-5" />
      </button>
    </div>
  )
}
